import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../../contexts/AuthContext';
import { X, Loader2, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { postLoginPath } from '@/utils/portalRouting';

interface RegisterFormProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
  embedded?: boolean;
}

const embeddedInputClass = 'bg-[#132035] border-white/10 text-white';
const embeddedLabelClass = 'text-white/70';

const RegisterForm: React.FC<RegisterFormProps> = ({ onClose, onSwitchToLogin, embedded = false }) => {
  const { register } = useAuth();
  const [location, navigate] = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    const result = await register(formData.name, formData.email, formData.password, formData.password_confirmation);

    if (result.success) {
      onClose();
      if (result.user) {
        const dest = postLoginPath(result.user, location);
        if (dest) navigate(dest);
      }
    } else {
      setError(result.message || 'Registration failed');
    }

    setIsLoading(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const errorAlert = error ? (
    embedded ? (
      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
        {error}
      </div>
    ) : (
      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {error}
      </div>
    )
  ) : null;

  const renderField = (
    id: string,
    label: string,
    input: React.ReactNode,
  ) => (
    <div>
      {embedded ? (
        <>
          <Label htmlFor={id} className={`${embeddedLabelClass} mb-1.5 block`}>
            {label}
          </Label>
          {input}
        </>
      ) : (
        <>
          <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
          {input}
        </>
      )}
    </div>
  );

  const renderPasswordInput = (
    id: string,
    name: string,
    value: string,
    show: boolean,
    onToggle: () => void,
    placeholder: string,
    minLength?: number,
  ) => {
    if (embedded) {
      return (
        <div className="relative">
          <Input
            type={show ? 'text' : 'password'}
            id={id}
            name={name}
            value={value}
            onChange={handleChange}
            required
            minLength={minLength}
            className={`${embeddedInputClass} pr-10`}
            placeholder={placeholder}
          />
          <button
            type="button"
            onClick={onToggle}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
          >
            {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      );
    }

    return (
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          required
          minLength={minLength}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    );
  };

  const formBody = (
    <>
      {errorAlert}

      <form onSubmit={handleSubmit} className="space-y-4">
        {renderField(
          'name',
          'Full Name',
          embedded ? (
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className={embeddedInputClass}
              placeholder="Enter your full name"
            />
          ) : (
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter your full name"
            />
          ),
        )}

        {renderField(
          'email',
          'Email Address',
          embedded ? (
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={embeddedInputClass}
              placeholder="Enter your email"
            />
          ) : (
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter your email"
            />
          ),
        )}

        <div>
          {renderField(
            'password',
            'Password',
            renderPasswordInput(
              'password',
              'password',
              formData.password,
              showPassword,
              () => setShowPassword(!showPassword),
              'Enter your password',
              8,
            ),
          )}
          <p className={embedded ? 'text-xs text-white/40 mt-1' : 'text-xs text-gray-500 mt-1'}>
            Must be at least 8 characters
          </p>
        </div>

        {renderField(
          'password_confirmation',
          'Confirm Password',
          renderPasswordInput(
            'password_confirmation',
            'password_confirmation',
            formData.password_confirmation,
            showConfirmPassword,
            () => setShowConfirmPassword(!showConfirmPassword),
            'Confirm your password',
            8,
          ),
        )}

        {embedded ? (
          <Button type="submit" disabled={isLoading} className="btn-cta w-full">
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        ) : (
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary text-white py-2 px-4 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        )}
      </form>

      <div className="mt-6 text-center">
        <p className={embedded ? 'text-white/60 text-sm' : 'text-gray-600'}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className={embedded ? 'text-[#D4FF00] hover:text-[#D4FF00]/80 font-medium transition-colors' : 'text-primary hover:text-primary/80 font-medium transition-colors'}
          >
            Sign in
          </button>
        </p>
      </div>
    </>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Create Account</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        {formBody}
      </div>
    </div>
  );
};

export default RegisterForm;
